import React from 'react';
import { logError } from '@utils/usefulFuncs';
import { VAULT_FACETS, initialFilterState } from '@utils/documentVault';
import { parseFilters } from '@utils/documentVaultUrl';

export { VAULT_FACETS, VAULT_VIEW_MODES, DEFAULT_PAGE_SIZE, initialFilterState } from '@utils/documentVault';

export const FILTER_ACTIONS = {
  SET_SEARCH: 'SET_SEARCH',
  TOGGLE_FACET_VALUE: 'TOGGLE_FACET_VALUE',
  CLEAR_FACET: 'CLEAR_FACET',
  SET_DATE_RANGE: 'SET_DATE_RANGE',
  SET_CLEAN_ELIGIBLE_ONLY: 'SET_CLEAN_ELIGIBLE_ONLY',
  SET_SORT: 'SET_SORT',
  SET_PAGE: 'SET_PAGE',
  SET_VIEW_MODE: 'SET_VIEW_MODE',
  SET_DENSITY: 'SET_DENSITY',
  CLEAR_ALL: 'CLEAR_ALL',
  HYDRATE_FROM_URL: 'HYDRATE_FROM_URL',
};

function toggleValue(values, value) {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

export function reducer(state, action) {
  switch (action.type) {
    case FILTER_ACTIONS.SET_SEARCH: {
      // Any narrowing returns the user to the first page; page 4 of the old result set is
      // meaningless against the new one.
      return { ...state, search: action.payload, page: 1 };
    }

    case FILTER_ACTIONS.TOGGLE_FACET_VALUE: {
      const { facet, value } = action.payload;
      if (!(facet in VAULT_FACETS)) return state;
      return { ...state, [facet]: toggleValue(state[facet], value), page: 1 };
    }

    case FILTER_ACTIONS.CLEAR_FACET: {
      const facet = action.payload;
      if (!(facet in VAULT_FACETS)) return state;
      return { ...state, [facet]: [], page: 1 };
    }

    case FILTER_ACTIONS.SET_DATE_RANGE: {
      return { ...state, dateRange: { ...initialFilterState.dateRange, ...action.payload }, page: 1 };
    }

    case FILTER_ACTIONS.SET_CLEAN_ELIGIBLE_ONLY: {
      return { ...state, cleanEligibleOnly: !!action.payload, page: 1 };
    }

    case FILTER_ACTIONS.SET_SORT: {
      const { key } = action.payload;
      const direction =
        action.payload.direction || (state.sort.key === key && state.sort.direction === 'desc' ? 'asc' : 'desc');
      return { ...state, sort: { key, direction }, page: 1 };
    }

    case FILTER_ACTIONS.SET_PAGE: {
      return { ...state, page: Math.max(1, action.payload) };
    }

    case FILTER_ACTIONS.SET_VIEW_MODE: {
      return { ...state, viewMode: action.payload };
    }

    case FILTER_ACTIONS.SET_DENSITY: {
      return { ...state, density: action.payload };
    }

    case FILTER_ACTIONS.CLEAR_ALL: {
      // View mode, density, and sort are display preferences, not filters -- clearing filters
      // shouldn't yank the user back to a different layout.
      return {
        ...initialFilterState,
        viewMode: state.viewMode,
        density: state.density,
        sort: state.sort,
      };
    }

    case FILTER_ACTIONS.HYDRATE_FROM_URL: {
      return { ...initialFilterState, ...action.payload };
    }

    default:
      throw new Error(`missing case: DocumentVaultFilterContext (${action.type})`);
  }
}

const defaultState = {
  filters: initialFilterState,
  setSearch: () => logError('setSearch called without a DocumentVaultFilterProvider'),
  toggleFacetValue: () => logError('toggleFacetValue called without a DocumentVaultFilterProvider'),
  clearFacet: () => logError('clearFacet called without a DocumentVaultFilterProvider'),
  setDateRange: () => logError('setDateRange called without a DocumentVaultFilterProvider'),
  setCleanEligibleOnly: () => logError('setCleanEligibleOnly called without a DocumentVaultFilterProvider'),
  setSort: () => logError('setSort called without a DocumentVaultFilterProvider'),
  setPage: () => logError('setPage called without a DocumentVaultFilterProvider'),
  setViewMode: () => logError('setViewMode called without a DocumentVaultFilterProvider'),
  setDensity: () => logError('setDensity called without a DocumentVaultFilterProvider'),
  clearAll: () => logError('clearAll called without a DocumentVaultFilterProvider'),
  hydrateFromUrl: () => logError('hydrateFromUrl called without a DocumentVaultFilterProvider'),
};

export const DocumentVaultFilterContext = React.createContext(defaultState);

/*
  Hydrating in the initializer rather than in an effect means a shared link renders its filtered
  view on the first paint -- no flash of the full document list.
*/
function initFromUrl() {
  if (typeof window === 'undefined' || !window.location?.search) return initialFilterState;
  return { ...initialFilterState, ...parseFilters(window.location.search) };
}

export const DocumentVaultFilterProvider = ({ children }) => {
  const [filters, dispatch] = React.useReducer(reducer, undefined, initFromUrl);

  const setSearch = React.useCallback((value) => {
    dispatch({ type: FILTER_ACTIONS.SET_SEARCH, payload: value });
  }, []);

  const toggleFacetValue = React.useCallback((facet, value) => {
    dispatch({ type: FILTER_ACTIONS.TOGGLE_FACET_VALUE, payload: { facet, value } });
  }, []);

  const clearFacet = React.useCallback((facet) => {
    dispatch({ type: FILTER_ACTIONS.CLEAR_FACET, payload: facet });
  }, []);

  const setDateRange = React.useCallback((dateRange) => {
    dispatch({ type: FILTER_ACTIONS.SET_DATE_RANGE, payload: dateRange });
  }, []);

  const setCleanEligibleOnly = React.useCallback((value) => {
    dispatch({ type: FILTER_ACTIONS.SET_CLEAN_ELIGIBLE_ONLY, payload: value });
  }, []);

  const setSort = React.useCallback((key, direction) => {
    dispatch({ type: FILTER_ACTIONS.SET_SORT, payload: { key, direction } });
  }, []);

  const setPage = React.useCallback((page) => {
    dispatch({ type: FILTER_ACTIONS.SET_PAGE, payload: page });
  }, []);

  const setViewMode = React.useCallback((viewMode) => {
    dispatch({ type: FILTER_ACTIONS.SET_VIEW_MODE, payload: viewMode });
  }, []);

  const setDensity = React.useCallback((density) => {
    dispatch({ type: FILTER_ACTIONS.SET_DENSITY, payload: density });
  }, []);

  const clearAll = React.useCallback(() => {
    dispatch({ type: FILTER_ACTIONS.CLEAR_ALL });
  }, []);

  const hydrateFromUrl = React.useCallback((parsed) => {
    dispatch({ type: FILTER_ACTIONS.HYDRATE_FROM_URL, payload: parsed });
  }, []);

  const value = React.useMemo(
    () => ({
      filters,
      setSearch,
      toggleFacetValue,
      clearFacet,
      setDateRange,
      setCleanEligibleOnly,
      setSort,
      setPage,
      setViewMode,
      setDensity,
      clearAll,
      hydrateFromUrl,
    }),
    [
      filters,
      setSearch,
      toggleFacetValue,
      clearFacet,
      setDateRange,
      setCleanEligibleOnly,
      setSort,
      setPage,
      setViewMode,
      setDensity,
      clearAll,
      hydrateFromUrl,
    ]
  );

  return <DocumentVaultFilterContext.Provider value={value}>{children}</DocumentVaultFilterContext.Provider>;
};
